import { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Form } from 'antd';

import { GenericDataFormContext, LOADING_SAVED_QUOTATIONS_STATES, PLAN_TYPE, PlanType, QuotationInfoContext, QuotationStep, StepOneFormContext } from '../../../../../shared';
import { StepTwoFormContext } from './StepTwoFormContext';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import { Feature, FeaturesVariousRisksRequest, Products, SelectOption } from '../../../../../../../../../shared/interfaces';
import { HeaderTabConsortium } from '../../components/ChildrenTabConsortium/components';
import { ChildrenTabConsortium } from '../../components';
import { displayErrorMessage } from '../../../../../../../../../shared/utils';
import { LOADING_RENEWAL_QUOTATION_STATES } from '../../../../../shared/interfaces/loading-renewal-quotation-states-enum';
import { fixEncoding } from '../../../../../../../../../shared/services/insurancePolicy/utils';

const planTypeEquivalent: { A: boolean, C: boolean } = {
  A: true,
  C: false
};

export const StepTwoFormProvider: FC<PropsWithChildren> = ({ children }) => {
  const { quotationInfo, currentStep, loadSaveQuotationState, setLoadSaveQuotationState, savedQuotation, renewalQuotationState, setRenewalQuotationState, quotationToRenewal, setCurrentStep } = useContext( QuotationInfoContext );
  const { 
    getLocalitiesPerProvince, 
    setCanShowSpinner, 
    setPlansSelected, 
    updateFormsAfterRemoveTabs,
    removedTabIndexes,
    setRemovedTabIndexes,
    setUserCanDeleteTabs
  } = useContext( GenericDataFormContext );

  const INITIAL_KEY_TAB = 1;
  const [activeKey, setActiveKey] = useState<number>(INITIAL_KEY_TAB);
  const [ consortiumFormValues, setConsortiumFormValues ] = useState<any>();
  const [ plansByActivityList, setPlansByActivityList ] = useState<Array<Products>>([]);
  const [ planOptionsBySelectedPlan, setPlanOptionsBySelectedPlan ] = useState<Array<SelectOption>>([]);
  const [ planTypeSelected, setPlanTypeSelected ] = useState<PlanType>(PLAN_TYPE.OPEN);
  const [ isPlanTypeADisabled, setIsPlanTypeADisabled ] = useState(false);
  const [ isPlanTypeCDisabled, setIsPlanTypeCDisabled ] = useState(false);
  const [ areTabActionsAllowed, setAreTabActionsAllowed ] = useState<boolean>(true);
  const [ showModal, setShowModal ] = useState<boolean>(false);
  const [ keyTabToDelete, setKeyTabToDelete ] = useState<number | null>(null);
  const [tabs, setTabs] = useState<Array<any>>([]);
  const [canShouldUpdateTabs, setCanShouldUpdateTabs] = useState(true);
  const { clientSelected } = useContext(StepOneFormContext);
  const [allFeatures, setAllFeatures] =
  useState<
    Array<{ plan: string; keyTab: number; features: Array<Feature> }>
  >([]);
  const [stepTwoForm] = Form.useForm<any>();

  const newTabIndex = useRef(INITIAL_KEY_TAB);

  const addNewTab = useCallback(() => {
    if (tabs.length <= 5) {
      const newActiveKey = ++newTabIndex.current;

      setTabs([...tabs, {
        label: <HeaderTabConsortium id={newActiveKey} areTabActionsAllowed={areTabActionsAllowed} />,
        children: <ChildrenTabConsortium id={newActiveKey} />,
        key: newActiveKey,
        closable: false,
        forceRender: true
      }]);

      setActiveKey(newActiveKey);

      if(removedTabIndexes.includes(newActiveKey)) {
        setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
      };

      return
    }

    displayErrorMessage("Solo puede agregar hasta 6 Consorcios");
  }, [
    tabs, 
    newTabIndex, 
    setTabs, 
    setActiveKey, 
    planTypeSelected,
    removedTabIndexes,
    setRemovedTabIndexes,
  ]);

  const duplicateTab = useCallback((id: any) => {
    if (tabs.length <= 5) {
      const tabSelected = tabs.find((tab: any) => tab.key === id);
      const newActiveKey = ++newTabIndex.current;

      if (tabSelected) {
        const newTab = {
          ...tabSelected,
          key: newActiveKey,
          label: <HeaderTabConsortium id={newActiveKey} areTabActionsAllowed={areTabActionsAllowed} />,
          children: <ChildrenTabConsortium id={newActiveKey} duplicateId={id} />,
          closable: false,
          forceRender: true
        }

        setTabs([...tabs, newTab]);
        setActiveKey(newActiveKey);
        if(removedTabIndexes.includes(newActiveKey)) {
          setRemovedTabIndexes((prevValues) => prevValues.filter(el => el !== newActiveKey));
        };
      }

      return
    }

    displayErrorMessage("Solo puede agregar hasta 6 Consorcios.");
  }, [
    tabs, 
    newTabIndex, 
    setTabs, 
    setActiveKey, 
    planTypeSelected,
    removedTabIndexes,
    setRemovedTabIndexes,
  ]);


  const removeTab = useCallback((targetKey: number) => {
    setCanShowSpinner(true);
    setTimeout(() => {
      updateFormsAfterRemoveTabs([targetKey], [stepTwoForm]);

      setRemovedTabIndexes((prevValues) => [ ...prevValues, targetKey ]);

      const targetIndex = tabs.findIndex((pane: any) => pane.key === targetKey);
      const newPanes = tabs.filter((pane: any) => pane.key !== targetKey).map((tab, index) => ({
        ...tab,
        key: index + 1,
        label: <HeaderTabConsortium id={index + 1} areTabActionsAllowed={areTabActionsAllowed}/>,
        children: <ChildrenTabConsortium id={index + 1} />,
      }));
      if (newPanes.length && targetKey === activeKey) {
        const { key } = newPanes[targetIndex === newPanes.length ? targetIndex - 1 : targetIndex];
        setActiveKey(key);
      }
      newTabIndex.current = tabs.length - 1; 

      setTabs(newPanes);
      setCanShowSpinner(false);
    }, 500);
  }, [tabs, setTabs, setActiveKey, activeKey]);

  const resetTabs = useCallback((unconfirmedPlanType: PlanType) => {
    const isTabActionEnabled = planTypeEquivalent[unconfirmedPlanType];

    setTabs([
      { 
        label: <HeaderTabConsortium id={INITIAL_KEY_TAB} areTabActionsAllowed={isTabActionEnabled} />, 
        children: <ChildrenTabConsortium id={INITIAL_KEY_TAB} />, 
        key: INITIAL_KEY_TAB, 
        closable: false,
        forceRender: true
      }
    ]);

    stepTwoForm.resetFields();
    newTabIndex.current = INITIAL_KEY_TAB;
    setActiveKey(INITIAL_KEY_TAB);
  }, [tabs, setTabs, setActiveKey]);

  const getListPlansActivity = useCallback(async (article: string, branch: string) => {
    setCanShowSpinner(true);

    try {
      const response = await QuotationService.getProductsListPerProducer(article, branch);
      if (response?.data?.productosPorProductor?.length) {
        const productosPorProductor = response?.data?.productosPorProductor;

        setPlansByActivityList(productosPorProductor);
          
        const hasPlanTypeA = productosPorProductor.some(plan => plan.tipoDePlan === PLAN_TYPE.OPEN);
          
        setPlanTypeSelected(hasPlanTypeA ? PLAN_TYPE.OPEN : PLAN_TYPE.CLOSED);
      }
    } catch {
    } finally {
      setCanShowSpinner(false);
    }
  }, [setCanShowSpinner, setPlansByActivityList, setPlanTypeSelected]);

  const filterPlansByPlanTypeSelected = useCallback(() => {
    if (planTypeSelected) {
      const selectedPlanTypeValue: PlanType = planTypeSelected;

      if (!consortiumFormValues) {
        if (savedQuotation) {
          if (canShouldUpdateTabs) {
            const _tabs: any = [];
            
            savedQuotation.bienes.forEach((good: any) => {
              _tabs.push({
                label: <HeaderTabConsortium id={good.numeroComponente} areTabActionsAllowed={planTypeEquivalent?.[selectedPlanTypeValue]} />, 
                children: <ChildrenTabConsortium id={good.numeroComponente} />, 
                key: good.numeroComponente, 
                closable: false,
                forceRender: true
              });
            });

            setTabs([..._tabs]);
            setCanShouldUpdateTabs(false);
          }
        } else {
          setTabs([
            { 
              label: <HeaderTabConsortium id={INITIAL_KEY_TAB} areTabActionsAllowed={planTypeEquivalent[selectedPlanTypeValue]} />, 
              children: <ChildrenTabConsortium id={INITIAL_KEY_TAB} />, 
              key: INITIAL_KEY_TAB, 
              closable: false,
              forceRender: true
            }
          ]);
        }
      }
      setAreTabActionsAllowed(planTypeEquivalent[selectedPlanTypeValue]);
      const filteredPlans = plansByActivityList.filter(plan => plan.tipoDePlan === selectedPlanTypeValue);
      const plansOptions = filteredPlans.map((el) => ({
        label: `${el.codigo} - ${el.descripcionLarga}`,
        value: el.codigo
      }));
      setPlanOptionsBySelectedPlan(plansOptions);
    }
  }, [plansByActivityList, planTypeSelected]);

    const fillStepTwoData = useCallback(async (isRenewal:boolean) => {
      const _tabs: any = [];
      const _inputs: any = [];
      setUserCanDeleteTabs(false);
      let quotationToProccess;
      if(isRenewal){
        quotationToProccess = quotationToRenewal;
      }else{
        quotationToProccess = savedQuotation;
      }
      quotationToProccess.bienes.forEach((good: any, index: number) => {
        const _activeKey = index + 1;

        _tabs.push({
          label: <HeaderTabConsortium id={_activeKey}  areTabActionsAllowed={areTabActionsAllowed}/>,
          children: <ChildrenTabConsortium id={_activeKey} />,
          key: _activeKey,
          closable: false,
          forceRender: true,
        });

        _inputs.push({ 
          name: `plan-tab-${_activeKey}`, 
          value: `${good.planSeleccionado}` 
        });
        _inputs.push({ 
          name: `province-tab-${_activeKey}`, 
          value: `${good.provincia}` 
        });
        _inputs.push({ 
          name: `location-tab-${_activeKey}`, 
          value: `${good.sufijoPostal}${good.codigoPostal}` 
        });
        _inputs.push({
          name: `propertyDataAddress${_activeKey}`,
          value: `${good.domicilio}`,
        });
      });

      newTabIndex.current = quotationToProccess.bienes.length;
      setTabs([..._tabs ]);
      setActiveKey(INITIAL_KEY_TAB);
      stepTwoForm.setFields(_inputs);
    }, [
      savedQuotation,
      quotationToRenewal,
      setActiveKey,
      setTabs,
      stepTwoForm,
    ]);

  const fillStepTwoServices = useCallback(async (isRenewal:boolean) => {
    try {
      const promises: Array<Promise<void>> = [
        getListPlansActivity(quotationInfo!.article, quotationInfo!.branch)      
      ]

      //const plansSelected: string[] = [];
      const plansSelected: Array<{
        componentId: string | number;
        plan: string;
      }> = [];
      const provincesSelected: string[] = [];
      let quotationToProccess;

      if(isRenewal){
        quotationToProccess = quotationToRenewal;
      }else{
        quotationToProccess = savedQuotation;
      }
      quotationToProccess.bienes.forEach(async (good: any) => {
        if (!provincesSelected.includes(String(good.provincia))) {
          provincesSelected.push(String(good.provincia));
        }
      
        if (
          !plansSelected.some(
            (el) =>
              el.componentId === String(good.numeroComponente) &&
              el.plan === String(good.planSeleccionado)
          )
        ) {
          plansSelected.push({
            componentId: String(good.numeroComponente),
            plan: String(good.planSeleccionado),
          });
        }
      });
      //setPlansSelected(plansSelected);

      provincesSelected.forEach((province) => promises.push(getLocalitiesPerProvince(province)));
      
      plansSelected.forEach(({ componentId, plan }) => {
        promises.push(
          getFeaturesRiskByPlan(
            Number(componentId),
            plan,
            Number(componentId)
          )
        );
      });
      
      await Promise.all(promises).then(() => {
        if (isRenewal) {
          setRenewalQuotationState(LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL)
        }else{
          setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED);
        }
      })
    } catch  {}
  }, [
    getListPlansActivity,
    getLocalitiesPerProvince,
    quotationInfo,
    savedQuotation,
    setLoadSaveQuotationState,
    quotationToRenewal,
    setRenewalQuotationState,
    setPlansSelected,
  ]);

  const getFeaturesRisks = useCallback(
    async (
      planSelected: string,
      componente: number
    ): Promise<Array<Feature> | any> => {
      const params: FeaturesVariousRisksRequest = {
        numeroCotizacion: quotationInfo?.token!,
        rama: quotationInfo?.branch!,
        articuloRamaSecuencia: quotationInfo?.arse!,
        plan: planSelected,
        codigoPostal: clientSelected?.codigoPostal!,
        sufijoPostal: clientSelected?.sufijoCodigoPostal!,
        componente,
        ubicacionDeRiesgo: '',
      };

      try {
        let response = await QuotationService.postFeaturesVariousRisks(
          params
        );
        response.data.caracteristicas = response?.data?.caracteristicas.map(e => {
          return {
            ...e,
            descripcion: fixEncoding(e.descripcion)
          }
        })
        return response?.data?.caracteristicas ?? [];
      } catch (error) { }
    },
    [quotationInfo, clientSelected]
  );

  const getFeaturesRiskByPlan = useCallback(
    async (id: number, planSelected: string, componente: number) => {
      const isFeatureAlreadyAdded = allFeatures.some(
        (feature) => feature.plan === planSelected
      );      

      if (isFeatureAlreadyAdded) return;

      setCanShowSpinner(true);

      try {
        let featuresList = await getFeaturesRisks(planSelected, componente);

        setAllFeatures((prevValues) => [
          ...prevValues,
          { plan: planSelected, keyTab: id, features: featuresList },
        ]);
      } catch (error) {
      } finally {
        setCanShowSpinner(false);
      }
    },
    [
      setCanShowSpinner, 
      setAllFeatures, 
      getFeaturesRisks
    ]
  );

  useEffect(() => {
    if (plansByActivityList.length && planTypeSelected) {
      filterPlansByPlanTypeSelected();
    }
  }, [filterPlansByPlanTypeSelected, planTypeSelected, plansByActivityList.length]);

  useEffect(() => {
    if (quotationInfo && currentStep === QuotationStep.TWO && !plansByActivityList.length) {
      getListPlansActivity(quotationInfo.article, quotationInfo.branch);
    }
  }, [quotationInfo, currentStep, getListPlansActivity, plansByActivityList])

  useEffect(() => {
    if (plansByActivityList.length) {
      const hasPlanTypeA = plansByActivityList.some(plan => plan.tipoDePlan === PLAN_TYPE.OPEN);
      const hasPlanTypeC = plansByActivityList.some(plan => plan.tipoDePlan === PLAN_TYPE.CLOSED);

      setIsPlanTypeADisabled(!hasPlanTypeA);
      setIsPlanTypeCDisabled(!hasPlanTypeC);
    }
  }, [plansByActivityList]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADING
      );
      fillStepTwoServices(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
      LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_SERVICES_LOADED
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING
      );    
      fillStepTwoData(false);
    }
  }, [loadSaveQuotationState]);

  useEffect(() => {
    if (
      loadSaveQuotationState ===
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLING &&
      tabs.length === savedQuotation.bienes.length
    ) {
      setLoadSaveQuotationState(
        LOADING_SAVED_QUOTATIONS_STATES.STEP_TWO_DATA_FILLED
      );
    }
  }, [loadSaveQuotationState, savedQuotation, tabs]);

  useEffect(() => {

    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.STEP_ONE_DATA_FILLED) {
      setCurrentStep(QuotationStep.TWO);
    }
  }, [loadSaveQuotationState]);

  /**
   * Renewal
   */

  useEffect(() => {
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_ONE_DATA_FILLED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADING_RENEWAL
      );
      fillStepTwoServices(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {    
    if (
      renewalQuotationState ===
      LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_SERVICES_LOADED_RENEWAL
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL
      );    
      fillStepTwoData(true);
    }
  }, [renewalQuotationState]);

  useEffect(() => {
    if (
      renewalQuotationState ===
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLING_RENEWAL &&
      tabs.length === quotationToRenewal.bienes.length
    ) {
      setRenewalQuotationState(
        LOADING_RENEWAL_QUOTATION_STATES.STEP_TWO_DATA_FILLED_RENEWAL
      );
    }
  }, [renewalQuotationState, quotationToRenewal, tabs]);

  const values = useMemo(
    () => ({
      stepTwoForm,
      activeKey,
      setActiveKey,
      tabs,
      setTabs,
      duplicateTab,
      removeTab,
      addNewTab,
      keyTabToDelete,
      setKeyTabToDelete,
      showModal,
      setShowModal,
      planOptionsBySelectedPlan,
      areTabActionsAllowed,
      planTypeSelected,
      setPlanTypeSelected,
      resetTabs,
      consortiumFormValues,
      setConsortiumFormValues,
      isPlanTypeADisabled,
      isPlanTypeCDisabled,
      allFeatures,
      setAllFeatures,
      getFeaturesRisks,
      getFeaturesRiskByPlan
    }),
    [
      stepTwoForm,
      activeKey,
      setActiveKey,
      tabs,
      setTabs,
      duplicateTab,
      removeTab,
      addNewTab,
      keyTabToDelete,
      setKeyTabToDelete,
      showModal,
      setShowModal,
      planOptionsBySelectedPlan,
      areTabActionsAllowed,
      planTypeSelected,
      setPlanTypeSelected,
      resetTabs,
      consortiumFormValues,
      setConsortiumFormValues,
      isPlanTypeADisabled,
      isPlanTypeCDisabled,
      allFeatures,
      setAllFeatures,
      getFeaturesRisks,
      getFeaturesRiskByPlan
    ]
  );

  return (
    <StepTwoFormContext.Provider value={values}>
      {children}
    </StepTwoFormContext.Provider>
  );
};